// onSnapshot → TanStack Query. The queryFn opens the listener and resolves on the first snapshot;
// later snapshots are pushed with setQueryData. The listener closes when the query leaves the cache.
import { QueryClient, type QueryKey, queryOptions } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: Number.POSITIVE_INFINITY, retry: false, refetchOnWindowFocus: false } },
});

type Listen<T> = (next: (v: T) => void, fail: (e: Error) => void) => () => void;

const listeners = new Map<string, () => void>();

queryClient.getQueryCache().subscribe((ev) => {
  if (ev.type !== "removed") return;
  const h = ev.query.queryHash;
  listeners.get(h)?.();
  listeners.delete(h);
});

export function liveQuery<T>(queryKey: QueryKey, listen: Listen<T>) {
  return queryOptions<T>({
    queryKey,
    gcTime: 60_000,
    queryFn: ({ queryKey: key }) =>
      new Promise<T>((resolve, reject) => {
        const h = queryClient.getQueryCache().find({ queryKey: key, exact: true })?.queryHash ?? JSON.stringify(key);
        listeners.get(h)?.();
        let first = true;
        const unsub = listen(
          (v) => {
            markSynced();
            if (first) {
              first = false;
              resolve(v);
            } else queryClient.setQueryData(key, v);
          },
          (e) => {
            if (first) {
              first = false;
              reject(e);
            } else console.warn("live query error", key, e.message);
          },
        );
        listeners.set(h, unsub);
      }),
  });
}

/** Stop every listener (sign-out). */
export function closeAll() {
  for (const u of listeners.values()) u();
  listeners.clear();
  queryClient.clear();
}

// ---------------------------------------------------------------- "synced X ago"

let syncedAt = 0;
const subs = new Set<() => void>();
function markSynced() {
  syncedAt = Date.now();
  for (const s of subs) s();
}
export function useSyncedAt(): number {
  return useSyncExternalStore(
    (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
    () => syncedAt,
  );
}
